import { API_BASE } from '../apiBase';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function Logs() {
  const [logs, setLogs] = useState([]);
  const [displayedLogs, setDisplayedLogs] = useState(15); // Start with 15 logs
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      navigate('/inventory/login');
      return;
    }

    axios.get(`${API_BASE}/logs`, {
      headers: { Authorization: token }
    })
      .then(res => {
        console.log('Logs fetched:', res.data);
        setLogs(res.data || []);
      })
      .catch(err => {
        console.error('Fetch logs error:', err.response?.data || err.message);
        if (err.response?.status === 401) {
          alert('Session expired');
          localStorage.clear();
          navigate('/inventory/login');
        }
      });
  }, [navigate, token]);

  const handleShowMore = () => {
    setDisplayedLogs(prev => prev + 15); // Load 15 more each time
  };

  const handleClearLog = async () => {
    if (window.confirm('Are you sure you want to clear the log?')) {
      const adminPassword = window.prompt('Please enter your admin password:');
      if (adminPassword === null) return;
      try {
        await axios.delete(`${API_BASE}/logs`, {
          headers: { Authorization: token },
          data: { adminPassword }
        });
        setLogs([]);
        setDisplayedLogs(15); // Reset display count after clear
        alert('Logs cleared');
      } catch (err) {
        alert('Failed to clear logs');
      }
    }
  };

  const remainingLogs = logs.length - displayedLogs;
  const showMoreDisabled = remainingLogs <= 0;

  return (
    <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-4">
      <h1 className="text-3xl max-md:text-2xl font-bold mb-6">Transaction Logs</h1>

      {/* Buttons */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '15px' }}>
        <button
          onClick={() => navigate('/inventory')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0069d9')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#007bff')}
        >
          Back to Inventory
        </button>

        <button
          onClick={handleClearLog}
          style={{
            padding: '8px 16px',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            transition: 'background-color 0.2s',
          }}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#c82333')}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#dc3545')}
        >
          Clear All Logs
        </button>
      </div>

      {logs.length === 0 ? (
        <p className="mt-5 text-lg text-zinc-400">No logs available</p>
      ) : (
        <>
          {/* ===== MOBILE CARD LIST (phones only) ===== */}
          <div className="md:hidden space-y-2.5 mt-2">
            {logs.slice(0, displayedLogs).map((log) => {
              const isWithdraw = String(log.action).toLowerCase().includes('withdraw');
              return (
                <div key={log._id} className="bg-gray-900 border border-gray-800/60 rounded-xl p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-zinc-100 font-semibold text-sm min-w-0 break-words">
                      {log.itemId ? log.itemId.name : 'Item Deleted'}
                    </p>
                    <span className={`shrink-0 px-2.5 py-0.5 text-[11px] font-bold rounded-full ${
                      isWithdraw ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {isWithdraw ? '−' : '+'}{log.quantity} {log.action}
                    </span>
                  </div>
                  <p className="text-zinc-400 text-xs mt-1">
                    {log.employee}
                    {log.itemId?.location ? ` · ${log.itemId.location}` : ''}
                  </p>
                  <p className="text-zinc-600 text-xs mt-0.5">{new Date(log.timestamp).toLocaleString()}</p>
                </div>
              );
            })}
          </div>

          {/* ===== DESKTOP TABLE (dark-theme legible) ===== */}
          <div className="max-md:hidden mt-5 bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-800/60">
              <thead className="bg-gray-950/40">
                <tr>
                  {['#', 'Time', 'Employee', 'Action', 'Qty', 'Item', 'Location'].map(h => (
                    <th key={h} className="px-5 py-4 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/40">
                {logs.slice(0, displayedLogs).map((log, index) => (
                  <tr key={log._id} className="hover:bg-gray-800/40 transition-colors">
                    <td className="px-5 py-3.5 text-sm font-bold text-zinc-500">{index + 1}</td>
                    <td className="px-5 py-3.5 text-sm text-zinc-300 whitespace-nowrap">{new Date(log.timestamp).toLocaleString()}</td>
                    <td className="px-5 py-3.5 text-sm text-zinc-200">{log.employee}</td>
                    <td className={`px-5 py-3.5 text-sm font-medium ${String(log.action).toLowerCase().includes('withdraw') ? 'text-red-400' : 'text-emerald-400'}`}>{log.action}</td>
                    <td className="px-5 py-3.5 text-sm text-zinc-200">{log.quantity}</td>
                    <td className="px-5 py-3.5 text-sm text-zinc-200">{log.itemId ? log.itemId.name : <span className="text-zinc-500 italic">Item Deleted</span>}</td>
                    <td className="px-5 py-3.5 text-sm text-zinc-400">{log.itemId ? log.itemId.location : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Show More Button */}
          {!showMoreDisabled && (
            <button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                padding: '10px 24px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '1em',
              }}
            >
              Show More
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default Logs;